<template>
  <div class="min-h-screen">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div>
        <div class="flex items-center justify-between element-island">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">
              {{ $t('HeadContract.IndirectBillOfQuantities.title') }}
            </h1>

            <p class="mt-2 text-sm text-gray-600">
              {{ $t('HeadContract.IndirectBillOfQuantities.subTitle') }}
            </p>
          </div>
        </div>
      </div>

      <div
        v-if="trades.length === 0 && fetchTradesAction.is(States.COMPLETE)"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8"
      >
        <div class="px-6 py-16 text-center">
          <div class="mx-auto h-12 w-12 text-gray-300 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
          </div>

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            {{ $t('HeadContract.IndirectBillOfQuantities.noDevelopmentCostsFound') }}
          </h3>

          <p class="text-sm text-gray-500 mb-6">
            {{ $t('HeadContract.IndirectBillOfQuantities.createFirstDevelopmentCost') }}
          </p>

          <button
            type="button"
            class="btn btn--secondary-blue"
            @click="createTrade"
          >
            {{ $t('HeadContract.IndirectBillOfQuantities.button.addFirstDevelopmentCost') }}
          </button>
        </div>
      </div>

      <div
        v-else-if="fetchTradesAction.is(States.COMPLETE)"
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8"
      >
        <div class="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-5 h-5 text-gray-600">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
    />
</svg>

                <h3 class="text-lg font-semibold text-gray-900">
                  {{ $t('HeadContract.IndirectBillOfQuantities.title') }}
                </h3>
              </div>

              <Transition
                mode="out-in"
                enter-active-class="animate__animated animate__fadeIn animate__faster"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
              >
                <p
                  v-if="trades.length > 0"
                  key="total-trades"
                  class="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border border-gray-200"
                >
                  <span class="font-medium text-gray-900">{{ trades.length }}&nbsp;</span>

                  <span class="text-gray-500">{{ $t('HeadContract.IndirectBillOfQuantities.cost', trades.length) }}</span>
                </p>
              </Transition>
            </div>

            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <button
                  v-if="projectsStore.project?.unarchived"
                  type="button"
                  class="btn btn--secondary-blue flex items-center space-x-2"
                  @click="createTrade"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                  <span>{{ $t('HeadContract.IndirectBillOfQuantities.button.addDevelopmentCost') }}</span>
                </button>
              </div>

              <div class="text-right">
                <p class="text-sm text-gray-500">
                  Total Costs
                </p>

                <p class="text-lg font-semibold text-gray-900">
                  {{ australianCurrency(contractAmount) }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="overflow-x-auto">
          <div class="w-full">
            <Sortable
              :list="trades"
              item-key="id"
              class="divide-y divide-gray-200"
              :class="{ 'opacity-50 pointer-events-none': updateSortAction.is(States.LOADING) }"
              :disabled="disableDraggable || !projectsStore.project?.unarchived"
              handle=".drag-handle"
              @end="changeSortOrder"
            >
              <template
                v-if="updateSortAction.is(States.LOADING)"
                #header
              >
                <div class="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-1/2">
                  <AppSpinner />
                </div>
              </template>

              <template #item="{element}">
                <div class="group relative">
                  <TradeRow
                    :disable-draggable="!projectsStore.project?.unarchived"
                    :trade="element"
                    @edit="selectTrade(element)"
                    @delete="deleteTrade(element)"
                    @modal-close="disableDraggable = false"
                    @modal-open="disableDraggable = true"
                    @create-trade-item="handleCreateTradeItem"
                    @edit-trade-item="handleEditTradeItem"
                  >
                    <template #default="{ open }">
                      <div
                        v-if="projectsStore.project?.unarchived"
                        class="group-hover:bg-gray-50 py-0.5 transition-colors duration-150 ease-in-out"
                      >
                        <button
                          v-if="open"
                          type="button"
                          class="mx-auto flex items-center justify-center space-x-1 cursor-pointer opacity-0 group-hover:opacity-100 text-xs font-medium border border-blue-200 rounded-lg px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all duration-300 ease-in-out shadow-sm hover:shadow-md transform"
                          @click="handleCreateTradeItem({ trade: element })"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 fill-blue-700">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                          <span>{{ $t('HeadContract.IndirectBillOfQuantities.button.addTradeItem') }}</span>
                        </button>

                        <button
                          v-else
                          type="button"
                          class="mx-auto flex items-center justify-center space-x-1 cursor-pointer opacity-0 group-hover:opacity-100 text-xs font-medium border border-blue-200 rounded-lg px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-all duration-300 ease-in-out shadow-sm hover:shadow-md transform"
                          @click="showTradeAndSelectPosition(element.sort_order + 1)"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4 fill-blue-700">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

                          <span>{{ $t('HeadContract.IndirectBillOfQuantities.button.addTrade') }}</span>
                        </button>
                      </div>
                    </template>
                  </TradeRow>
                </div>
              </template>
            </Sortable>
          </div>
        </div>
      </div>

      <div
        v-else
        class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8"
      >
        <div class="px-6 py-16 text-center">
          <AppSpinner class="h-8 w-8 mx-auto mb-4" />

          <h3 class="text-sm font-medium text-gray-900 mb-1">
            {{ $t('HeadContract.IndirectBillOfQuantities.loadingTrades') }}
          </h3>
        </div>
      </div>
    </div>

    <TradeSaveModal
      v-if="showTradeModal"
      :trade="selectedTrade"
      :project-id="$props.projectId"
      :selected-position="selectedPosition"
      boq-type="indirect"
      @update-sort="changeSortOrderForTrade($event.trade, $event.index)"
      @close="showTradeModal = false; selectedPosition = undefined"
    />

    <TradeItemSaveModal
      v-if="showTradeItemModal"
      :trade="selectedTradeForItem"
      :selected-position="selectedPosition"
      :trade-item="selectedTradeItem"
      @update-sort="changeSortOrderForTradeItem($event.tradeItem, $event.index)"
      @close="showTradeItemModal = false; selectedTradeItem = undefined; disableDraggable = false"
    />
  </div>
</template>

<script setup lang="ts">
import TradeItemSaveModal from '@/components/HeadContract/IndirectBillOfQuantities/TradeItemSaveModal.vue';
import TradeRow from '@/components/HeadContract/IndirectBillOfQuantities/TradeRow.vue';
import TradeSaveModal from '@/components/HeadContract/IndirectBillOfQuantities/TradeSaveModal.vue';
import { useProjectTypeRedirect } from '@/composables/useProjectTypeRedirect';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency } from '@/helpers';
import Trade from '@/models/Trade';
import TradeItem from '@/models/TradeItem';
import { useProjectsStore } from '@/store/projects';
import { useTradeItemsStore } from '@/store/tradeItems';
import { useTradesStore } from '@/store/trades';
import Icon from '@/svgs/Icon.vue';
import { Sortable } from 'sortablejs-vue3';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  projectId: string;
}>();

const tradesStore = useTradesStore();
const projectsStore = useProjectsStore();
const tradeItemsStore = useTradeItemsStore();
const fetchTradesAction = useStoreApiAction(tradesStore.fetchTrades);

watch(() => props.projectId, () => {
  fetchTradesAction.request(props.projectId, { boq_type: 'indirect' }).catch((error) => {
    console.log(error);
  });
}, { immediate: true });

const trades = computed(() => {
  return tradesStore.models
    .with('trade_items')
    .where('boq_type', 'indirect')
    .where('project_id', parseInt(props.projectId))
    .orderBy('sort_order')
    .get();
});

const contractAmount = computed(() => {
  return trades.value.reduce((total, trade) => {
    return total + trade.totalBudget;
  }, 0);
});

const selectedTrade = ref<Trade | undefined>();
const selectedPosition = ref<number | undefined>();
const showTradeModal = ref(false);
// Trade Item Modal State
const selectedTradeForItem = ref<Trade | undefined>();
const selectedTradeItem = ref<TradeItem | undefined>();
const showTradeItemModal = ref(false);

const showTradeAndSelectPosition = (position: number) => {
  selectedPosition.value = position;
  showTradeModal.value = true;
};

const selectTrade = (trade: Trade) => {
  selectedTrade.value = trade;
  showTradeModal.value = true;
};

const createTrade = () => {
  selectedTrade.value = undefined;
  showTradeModal.value = true;
};

const deleteTradeAction = useStoreApiAction(tradesStore.deleteTrade);

const deleteTrade = (trade: Trade) => {
  Swal.fire({
    icon: 'warning',
    title: 'Delete Indirect Cost?',
    text: `You are about to delete ${trade.name}. This action cannot be undone.`,
    showCancelButton: true,
    showConfirmButton: true,
  }).then((result) => {
    if(result.isConfirmed) {
      deleteTradeAction.request(trade.id).then(() => {
        Swal.fire({
          icon: 'warning',
          title: 'Indirect Cost Deleted.',
        });
      }).catch((error) => {
        if(error?.status === 422 && error?.data.errors.trade_items) {
          Swal.fire({
            icon: 'error',
            title: `Failed to delete indirect cost ${trade.name}`,
            text: error?.data.errors.trade_items[0],
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: `Failed to delete indirect cost ${trade.name}`,
          });
        }
      });
    }
  });
};

const updateSortAction = useStoreApiAction(tradesStore.updateSort);

const changeSortOrderForTrade = (trade: Trade, index: number) => {
  updateSortAction.request(trade.id, {
    sort_order: index,
  }).then((data) => {
    console.log(data);
  }).catch((error) => {
    Swal.fire({
      title: 'Something went wrong.',
      icon: 'error',
    });
  });
};

const updateTradeItemSortAction = useStoreApiAction(tradeItemsStore.updateSort);

const changeSortOrderForTradeItem = (tradeItem: TradeItem, index: number) => {
  updateTradeItemSortAction.request(tradeItem.id, {
    sort_order: index,
  }).catch((error) => {
    Swal.fire({
      title: 'Something went wrong.',
      icon: 'error',
    });
  });
};

const handleCreateTradeItem = (data: { trade: Trade }) => {
  selectedTradeForItem.value = data.trade;
  selectedTradeItem.value = undefined;
  showTradeItemModal.value = true;
  disableDraggable.value = true;
};

const handleEditTradeItem = (data: { tradeItem: TradeItem; trade: Trade }) => {
  selectedTradeForItem.value = data.trade;
  selectedTradeItem.value = data.tradeItem;
  showTradeItemModal.value = true;
  disableDraggable.value = true;
};

const changeSortOrder = (e) => {
  const item = trades.value.at(e.oldIndex);

  changeSortOrderForTrade(item, e.newIndex + 1);
};

const disableDraggable = ref(false);

useProjectTypeRedirect();
</script>

<style scoped></style>
